<template>
    <div class="panel-contents">
        <div class="d-flex align-items-center">
            <h3 v-if="thread">{{ thread.predicted_subject }}</h3>
            <div v-else class="d-flex">
                <div
                    class="spinner-grow spinner-grow-sm text-secondary me-2"
                    role="status"
                ></div>
                <div
                    class="spinner-grow spinner-grow-sm text-secondary me-2"
                    role="status"
                ></div>
                <div
                    class="spinner-grow spinner-grow-sm text-secondary"
                    role="status"
                ></div>
            </div>
        </div>

        <div class="thread-messages-container" ref="messagesContainer">
            <!-- Each transaction has both a USER message and a PATTY message -->
            <template v-if="thread">
                <template
                    v-for="transaction in thread.responses"
                    :key="transaction.id"
                >
                    <!-- User Message -->
                    <MessageBubble
                        :user="user"
                        align="right"
                        :created-at="transaction.created_at"
                    >
                        <template #content>
                            <CitationText
                                :message-text="transaction.input_text"
                            />
                        </template>
                    </MessageBubble>

                    <!-- Patty Message -->
                    <div
                        v-if="transaction.output_text"
                        class="d-flex flex-column"
                        style="gap: var(--space-2)"
                    >
                        <!-- Patty Response Message -->
                        <MessageBubble
                            align="left"
                            static-name="Patty"
                            static-image="patty/patty-avatar.svg"
                            :created-at="transaction.created_at"
                        >
                            <template #content>
                                <CitationText
                                    :message-text="transaction.output_text"
                                    :citations="transaction.annotations"
                                    @open-pdf="openPdf"
                                />
                            </template>
                        </MessageBubble>

                        <!-- Patty Response Message Actions -->
                        <div
                            class="d-flex flex-row align-items-center justify-content-end"
                            style="gap: var(--space-3); min-height: 24px"
                        >
                            <!-- Upvote Button -->
                            <button
                                class="action-btn"
                                @click="handleRate(transaction.id, 'GOOD')"
                                :class="{
                                    selected: transaction.rating === 'GOOD',
                                }"
                            >
                                <img
                                    v-if="transaction.rating === 'GOOD'"
                                    :src="thumbsUpFilled"
                                />
                                <i v-else class="fi fi-thumbs-up"></i>
                            </button>

                            <!-- Downvote Button -->
                            <button
                                class="action-btn"
                                @click="handleRate(transaction.id, 'BAD')"
                                :class="{
                                    selected: transaction.rating === 'BAD',
                                }"
                            >
                                <img
                                    v-if="transaction.rating === 'BAD'"
                                    :src="thumbsDownFilled"
                                />
                                <i v-else class="fi fi-thumbs-down"></i>
                            </button>

                            <!-- Copy Button -->
                            <button
                                class="action-btn"
                                @click="
                                    handleCopy(
                                        transaction.output_text,
                                        transaction.id
                                    )
                                "
                            >
                                <i
                                    v-if="copyingIds[transaction.id]"
                                    class="fi fi-check check-icon"
                                ></i>
                                <i v-else class="fi fi-copy"></i>
                            </button>
                        </div>
                    </div>
                </template>
            </template>

            <!-- LIVE Question - User Message which is currently being processed by Patty -->
            <template v-if="fetching">
                <MessageBubble
                    :user="user"
                    align="right"
                    :created-at="new Date().toISOString()"
                >
                    <template #content>
                        <CitationText :message-text="pattyStore.queryText" />
                    </template>
                </MessageBubble>

                <transition>
                    <MessageBubble
                        v-if="textStream"
                        align="left"
                        static-name="Patty"
                        static-image="patty/patty-avatar.svg"
                        :created-at="new Date().toISOString()"
                    >
                        <template #content>
                            <CitationText :message-text="textStream" />
                        </template>
                    </MessageBubble>
                </transition>
            </template>
        </div>

        <MessageField
            @submit="submitMessageToThread()"
            v-model="messageInput"
        ></MessageField>

        <!-- PDF Viewer -->
        <PdfViewerSidekick
            :visible="pdfVisible"
            :current-pdf-file="currentPdfFile"
            @close-pdf="closePdf"
        />
    </div>
</template>

<script>
import MessageBubble from '@/components/patty/MessageBubble.vue';
import PdfViewerSidekick from '@/components/pdf_viewer/PdfViewerSidekick.vue';
import CitationText from '@/components/patty/CitationText.vue';
import MessageField from '@/components/fields/MessageField.vue';
import { usePattyStore } from '@/stores/patty';

import thumbsUpFilled from '@/assets/images/icons/thumbs-up-filled.svg';
import thumbsDownFilled from '@/assets/images/icons/thumbs-down-filled.svg';

export default {
    name: 'PattyThread',
    components: {
        MessageBubble,
        PdfViewerSidekick,
        CitationText,
        MessageField,
    },
    props: {
        threadId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            pdfVisible: false,
            currentPdfFile: null,
            copyingIds: {},

            fetching: false,
            thread: null,
            pattyStore: null,

            textStream: '',
            messageInput: '',

            thumbsUpFilled,
            thumbsDownFilled,
        };
    },

    watch: {
        'pattyStore.threadId'() {
            if (this.pattyStore.threadId > 0) this.getThread();
            else this.createThread();
        },
    },

    computed: {
        user() {
            return this.$userStore.user;
        },
    },

    methods: {
        openPdf(fileHash) {
            this.pdfVisible = true;
            this.currentPdfFile = fileHash;
        },
        closePdf() {
            this.pdfVisible = false;
            this.currentPdfFile = null;
        },
        handleRate(transactionId, status) {
            const payload = {
                id: transactionId,
                rating: status,
            };

            this.$postData(
                `openai/assistant-thread/message/rate`,
                payload
            ).then((response) => {
                if (response.success) {
                    this.thread.responses.forEach((transaction) => {
                        if (transaction.id === transactionId)
                            transaction.rating = status;
                    });
                } else {
                    this.$alertStore.pushError(
                        this.$t('patty.alerts.rateMessageError')
                    );
                }
            });
        },
        handleCopy(message, messageId) {
            navigator.clipboard.writeText(message);
            this.copyingIds[messageId] = true;
            setTimeout(() => {
                this.copyingIds[messageId] = false;
            }, 1000);
        },

        scrollToBottom(keepOnly = false) {
            if (this.$refs.messagesContainer) {
                const container = this.$refs.messagesContainer;

                if (keepOnly) {
                    const atBottom =
                        container.scrollTop + container.clientHeight >=
                        container.scrollHeight;
                    if (atBottom) {
                        this.$nextTick(() => {
                            container.scrollTop = container.scrollHeight;
                        });
                    }
                } else {
                    container.scrollTop = container.scrollHeight;
                }
            }
        },

        submitMessageToThread() {
            if (this.fetching) return;

            this.textStream = '';
            this.fetching = true;

            const payload = {
                thread_id: this.thread.id,
                message: this.messageInput,
            };

            this.pattyStore.setQueryText(this.messageInput);
            this.messageInput = '';

            this.$nextTick(() => {
                this.scrollToBottom();
            });

            this.$postData(
                'openai/assistant-thread/message/create',
                payload,
                true,
                (chunk) => {
                    if (chunk.event == 'response-text') {
                        this.textStream += chunk.data;
                        this.scrollToBottom(true);
                    } else if (chunk.event == 'new-response') {
                        try {
                            const response = JSON.parse(chunk.data);
                            this.thread.responses.push(response);
                        } catch {
                            this.$alertStore.pushError(
                                this.$t('patty.alerts.parseResponseError')
                            );
                        }
                    }
                }
            ).then((response) => {
                this.fetching = false;
                if (response?.message) {
                    this.$alertStore.pushError(response.message);
                } else {
                    this.scrollToBottom(true);
                }
            });
        },

        createThread() {
            this.textStream = '';
            this.fetching = true;

            const payload = {
                message: this.pattyStore.queryText,
            };

            this.$nextTick(() => {
                this.scrollToBottom();
            });

            this.$postData(
                'openai/assistant-thread/create',
                payload,
                true,
                (chunk) => {
                    if (chunk.event == 'response-text') {
                        this.textStream += chunk.data;
                        this.scrollToBottom(true);
                    } else if (chunk.event == 'new-thread') {
                        try {
                            this.thread = JSON.parse(chunk.data);
                        } catch {
                            this.$alertStore.pushError(
                                this.$t('patty.alerts.parseResponseError')
                            );
                        }
                    }
                }
            ).then((response) => {
                this.fetching = false;

                if (response?.message) {
                    this.$alertStore.pushError(response.message);
                } else {
                    this.pattyStore.setQueryText('');
                    this.scrollToBottom(true);
                }
            });
        },

        getThread() {
            const threadId = this.pattyStore.threadId;
            this.fetching = true;

            const payload = {
                where: { id: threadId },
                include: { responses: true },
            };

            this.$getData('openai/assistant-thread/read', payload).then(
                (response) => {
                    if (response.success) {
                        this.thread = response.data.length
                            ? response.data[0]
                            : null;
                    } else if ('message' in response) {
                        this.$alertStore.pushError(response.message);
                    }

                    this.fetching = false;

                    this.$nextTick(() => {
                        this.scrollToBottom();
                    });
                }
            );
        },
    },

    created() {
        this.pattyStore = usePattyStore();
    },
};
</script>

<style scoped>
.v-enter-active,
.v-leave-active {
    transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
    opacity: 0;
}

.panel-contents {
    display: inline-flex;
    flex-direction: column;
    gap: var(--space-6);
    height: 100%;
    width: 100%;
}

.thread-messages-container {
    max-height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    flex-grow: 1;
    overflow-y: auto;
    padding-right: 0.5rem;
}

.thread-messages-container::-webkit-scrollbar {
    width: 3px;
    opacity: 0;
    transition: all 0.3s ease;
}

.thread-messages-container:hover::-webkit-scrollbar {
    opacity: 1;
}

.thread-messages-container::-webkit-scrollbar-track {
    background: var(--neutral-450);
    border-radius: var(--radius-sm);
}

.thread-messages-container::-webkit-scrollbar-thumb {
    background: var(--neutral-600);
    border-radius: var(--radius-sm);
}

.thread-messages-container::-webkit-scrollbar-thumb:hover {
    background: var(--neutral-700);
}

.separator {
    border-top: 1px solid var(--neutral-600);
}

.input-button {
    bottom: var(--space-2);
    right: var(--space-2);
}

.action-btn {
    background-color: transparent;
    border: none;
    cursor: pointer;
    padding: 0;
    height: 28px;
    width: 28px;
    position: relative;
    border-radius: 20%;
}

.action-btn img,
.action-btn i {
    transition: all 0.2s ease;
    width: var(--space-4);
    height: var(--space-4);
}

.action-btn:focus {
    outline: none;
}

.action-btn:hover {
    background-color: var(--primary-100);
    border: none;
    color: var(--neutral-900);
}

.selected {
    cursor: default;
    pointer-events: none;
}

.check-icon {
    animation: checkAppear 0.2s ease-in-out forwards;
}

@keyframes checkAppear {
    0% {
        transform: scale(0);
        opacity: 0;
    }
    70% {
        transform: scale(1.2);
    }
    100% {
        transform: scale(1);
        opacity: 1;
    }
}
</style>
